/**
 * @fileoverview Formulário do editor de triagem: converte a configuração do
 * backend (TriageDefinitionConfig) no estado da tela e de volta, e gera as
 * chaves internas a partir dos nomes. O backend valida tudo de novo.
 */

import stripAccents from "../../../../utils/stripAccents";

let seq = 0;
const uid = () => `u${Date.now().toString(36)}${(seq += 1)}`;

/** "Foto da Etiqueta!" → "foto_da_etiqueta" (letras minúsculas, números e _; começa com letra). */
export const slug = (text) => {
  let s = stripAccents(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (s && !/^[a-z]/.test(s)) s = `item_${s}`;
  return s.slice(0, 40).replace(/_+$/, "");
};

/** Chave válida (2 a 40) e diferente das já usadas. */
export const uniqueKey = (base, used) => {
  if (!base) return "";
  let key = base.length < 2 ? `${base}_1` : base;
  let n = 2;
  while (used.has(key)) {
    const suffix = `_${n}`;
    n += 1;
    key = `${base.slice(0, 40 - suffix.length)}${suffix}`;
  }
  return key;
};

export const newField = () => ({
  uid: uid(),
  keyTouched: false,
  key: "",
  label: "",
  type: "string",
  required: false,
  sensitive: false,
  // Dado da OS para comparar ("" = não compara).
  compareWith: "",
});

/** Item novo; com a lista de cada garantia, nasce na lista aberta ("in"/"out"). */
export const newItem = (warranty = "") => ({
  uid: uid(),
  keyTouched: false,
  open: true,
  key: "",
  name: "",
  description: "",
  inputType: "image",
  required: true,
  aiInstruction: "",
  requestMessage: "",
  retryMessage: "",
  maxAttempts: 3,
  onMaxAttempts: "handoff",
  extraction: { enabled: false, minConfidence: 80, fields: [] },
  // Dado da OS que já preenche o item e é confirmado com o cliente ("" = nenhum).
  workOrderField: "",
  // Descreve o problema do cliente (topo do painel do atendente, com o código de erro).
  isProblem: false,
  // Lista da garantia ("in"/"out"); "" = nas duas.
  warranty,
});

// Itens por garantia (aba Itens): em garantia e fora da garantia.
export const WARRANTIES = ["in", "out"];

/** O item está nesta lista? Item sem garantia vale nas duas. */
export const inWarrantyList = (item, warranty) => !item.warranty || item.warranty === warranty;

/** Os itens de uma lista: os dela e os que valem nas duas. */
export const itemsOfWarranty = (items, warranty) => items.filter((it) => inWarrantyList(it, warranty));

/**
 * Sobe (-1) ou desce (1) o item `index` da lista mostrada (`visible`, parte de
 * `items`): ele passa para antes (ou depois) do vizinho dela. Só ele muda de
 * lugar; a ordem dos outros, também na lista escondida, fica como estava.
 */
export const moveWithinList = (items, visible, index, delta) => {
  const item = visible[index];
  const next = items.filter((it) => it !== item);
  const pos = next.indexOf(visible[index + delta]);
  next.splice(delta < 0 ? pos : pos + 1, 0, item);
  return next;
};

/**
 * Itens por garantia desligado. Ao ligar, todos os itens valem nas duas listas
 * e, sem a garantia na OS, vale a de fora da garantia.
 */
export const emptyWarrantySplit = () => ({
  enabled: false,
  whenUnknown: "out",
  texts: { in: { completion: "", supportText: "" }, out: { completion: "", supportText: "" } },
});

/**
 * Início pela OS desligado. Ao ligar, começa no manual: o automático manda
 * mensagem para clientes e só liga escolhido. Horário: segunda a sábado, 8h às 20h.
 */
export const emptyWorkOrders = () => ({
  enabled: false,
  sourceId: "",
  startMode: "manual",
  statuses: [],
  types: [],
  days: [1, 2, 3, 4, 5, 6],
  start: "08:00",
  end: "20:00",
});

/**
 * Nome do contato montado pela triagem (aba "Cadastro do contato"), desligado.
 * As regras do formato ficam em contactNameFormat.js (as mesmas do servidor).
 */
export const emptyContactName = () => ({ enabled: false, format: "" });

/** A opção antiga da aba Ordens de serviço ("número da OS no nome do contato"). */
export const LEGACY_CONTACT_NAME_FORMAT = "{os} {nome}";

/** CRM desligado. Ao escolher o quadro, a aba já sugere a entrada e as concluídas. */
export const emptyCrm = () => ({
  enabled: false,
  boardId: "",
  entry: "",
  inProgress: "",
  attention: "",
  completed: "",
  rules: [],
});

export const newCrmRule = () => ({ uid: uid(), requirementKey: "", when: "awaiting", columnId: "" });

/** Envio do cliente ao Contele desligado (aba Conclusão). */
export const emptyCustomerExport = () => ({ enabled: false, integrationId: "", items: [] });

// Encaminhamento pelos dados da OS (aba Conclusão): até 20 regras, até 500 valores cada.
export const WORK_ORDER_ROUTE_FIELDS = ["model", "brand", "category", "type", "warranty", "city", "state"];
export const WORK_ORDER_ROUTE_MATCHES = ["equals", "startsWith", "contains"];
export const WORK_ORDER_ROUTE_MAX = 20;
export const WORK_ORDER_ROUTE_MAX_VALUES = 500;
export const WORK_ORDER_ROUTE_MAX_VALUE_LENGTH = 80;

// Dados por regra: o primeiro e mais dois ("E também"), cada dado uma vez.
export const WORK_ORDER_ROUTE_MAX_CONDITIONS = 3;

// Regra nova já aberta para editar; as que vêm salvas abrem fechadas (só o resumo).
export const newWorkOrderRoute = () => ({ uid: uid(), open: true, field: "model", match: "equals", valuesText: "", also: [], queueId: "", userId: "" });

export const newRouteCondition = (field) => ({ uid: uid(), field, match: "equals", valuesText: "" });

/** A lista com o item da posição `index` movido `delta` posições (subir: -1, descer: 1). */
export const moveInList = (list, index, delta) => {
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(index + delta, 0, item);
  return next;
};

/**
 * Valores colados ou digitados: um por linha (vírgula pode fazer parte do
 * valor), sem os vazios e sem repetidos (maiúsculas, acentos, espaços e
 * pontuação não contam, como no servidor: TriageServices/rules/text.ts).
 */
export const routeValues = (text) => {
  const seen = new Set();
  return String(text || "")
    .split(/\n+/)
    .map((value) => value.replace(/\s+/g, " ").trim())
    .filter((value) => {
      const key = stripAccents(value).toLowerCase().replace(/[^a-z0-9]/g, "");
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

// Tempo dos lembretes: salvo em minutos; na tela, em minutos, horas ou dias.
export const TIME_UNITS = { minutes: 1, hours: 60, days: 1440 };
export const FOLLOW_UP_MIN_MINUTES = 10;
export const FOLLOW_UP_MAX_MINUTES = 30 * 1440;
export const FOLLOW_UP_MAX_STAGES = 5;
// Prazo sem resposta da aba Conclusão quando o campo fica vazio (o servidor usa o mesmo).
export const DEFAULT_EXPIRE_AFTER_DAYS = 30;

/** Minutos → { amount, unit } na maior unidade exata (1440 → 1 dia). */
const tempoNaTela = (minutos) => {
  const m = Number(minutos) || 0;
  if (m && m % TIME_UNITS.days === 0) return { amount: m / TIME_UNITS.days, unit: "days" };
  if (m && m % TIME_UNITS.hours === 0) return { amount: m / TIME_UNITS.hours, unit: "hours" };
  return { amount: m || "", unit: "minutes" };
};

/** { amount, unit } da tela → minutos (null: vazio ou inválido). */
export const toMinutes = (amount, unit) => {
  if (amount === "" || amount === null || amount === undefined) return null;
  const n = Number(amount);
  return Number.isFinite(n) ? Math.round(n * (TIME_UNITS[unit] || 1)) : null;
};

export const newReminder = (amount = 1, unit = "days", message = "") => ({ uid: uid(), amount, unit, message, template: null });

/**
 * Lembretes desligados. Ao ligar, a aba sugere dois (4 horas e 1 dia sem
 * resposta). Depois do último, segue esperando; horário: segunda a sábado, 8h às 20h.
 */
export const emptyFollowUp = () => ({
  enabled: false,
  stages: [],
  finalAction: "wait",
  finalAmount: 3,
  finalUnit: "days",
  days: [1, 2, 3, 4, 5, 6],
  start: "08:00",
  end: "20:00",
});

const templateDaTela = (tpl) =>
  tpl ? { name: tpl.name, language: tpl.language, variables: { ...(tpl.variables || {}) } } : null;

export const emptyForm = (defaultModel) => ({
  name: "",
  description: "",
  model: defaultModel || "gpt-5-mini",
  replyMode: "humanized",
  waitTimeSeconds: 8,
  persona: "",
  supportText: "",
  aiUserId: "",
  items: [newItem()],
  intro: "",
  completion: "",
  ticketAction: "close",
  // Destino da conversa: na conclusão (só em "passar para a equipe") e na passagem para uma pessoa.
  completionQueueId: "",
  completionUserId: "",
  handoffQueueId: "",
  handoffUserId: "",
  workOrderRoutes: [],
  expireAfterDays: 30,
  outreach: { whatsappId: "", template: null },
  workOrders: emptyWorkOrders(),
  crm: emptyCrm(),
  followUp: emptyFollowUp(),
  customerExport: emptyCustomerExport(),
  contactName: emptyContactName(),
  warrantySplit: emptyWarrantySplit(),
});

/** Definição do backend → estado da tela. */
export const fromDefinition = (def) => {
  const c = def.config || {};
  const ai = c.ai || {};
  const reqs = [...(c.requirements || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  return {
    name: def.name || "",
    description: def.description || "",
    model: ai.model || "gpt-5-mini",
    replyMode: ai.replyMode || "humanized",
    waitTimeSeconds: ai.waitTimeSeconds ?? 8,
    persona: ai.persona || "",
    supportText: ai.supportText || "",
    aiUserId: c.aiUserId ?? "",
    items: reqs.map((r) => ({
      uid: uid(),
      keyTouched: true,
      open: false,
      key: r.key,
      name: r.name || "",
      description: r.description || "",
      inputType: r.inputType || "text",
      workOrderField: r.workOrderField || "",
      isProblem: r.isProblem === true,
      warranty: r.warranty || "",
      required: r.required !== false,
      aiInstruction: r.aiInstruction || "",
      requestMessage: r.requestMessage || "",
      retryMessage: r.retryMessage || "",
      maxAttempts: r.maxAttempts || 3,
      onMaxAttempts: r.onMaxAttempts || "handoff",
      extraction: r.extraction?.enabled
        ? {
            enabled: true,
            minConfidence: Math.round((r.extraction.minConfidence ?? 0.8) * 100),
            fields: (r.extraction.fields || []).map((f) => ({
              uid: uid(),
              keyTouched: true,
              key: f.key,
              label: f.label || "",
              type: f.type || "string",
              required: !!f.required,
              sensitive: !!f.sensitive,
              compareWith: f.compareWith || "",
            })),
          }
        : { enabled: false, minConfidence: 80, fields: [] },
    })),
    intro: c.messages?.intro || "",
    completion: c.messages?.completion || "",
    ticketAction: c.onComplete?.ticketAction || "close",
    completionQueueId: c.onComplete?.queueId ?? "",
    completionUserId: c.onComplete?.userId ?? "",
    handoffQueueId: c.onHandoff?.queueId ?? "",
    handoffUserId: c.onHandoff?.userId ?? "",
    workOrderRoutes: (c.workOrderRoutes || []).map((r) => ({
      uid: uid(),
      open: false,
      field: r.field || "model",
      match: r.match || "equals",
      valuesText: (r.values || []).join("\n"),
      also: (r.also || []).map((d) => ({
        uid: uid(),
        field: d.field || "model",
        match: d.match || "equals",
        valuesText: (d.values || []).join("\n"),
      })),
      queueId: r.queueId ?? "",
      userId: r.userId ?? "",
    })),
    // Versões de antes da etapa H3 não têm o prazo: vale o padrão (30).
    expireAfterDays: c.expireAfterDays ?? 30,
    outreach: {
      whatsappId: c.outreach?.whatsappId ?? "",
      template: c.outreach?.template
        ? {
            name: c.outreach.template.name,
            language: c.outreach.template.language,
            variables: { ...(c.outreach.template.variables || {}) },
          }
        : null,
    },
    workOrders: c.workOrders
      ? {
          enabled: true,
          sourceId: c.workOrders.sourceId ?? "",
          startMode: c.workOrders.startMode || "manual",
          statuses: [...(c.workOrders.statuses || [])],
          types: [...(c.workOrders.types || [])],
          days: [...(c.workOrders.sendWindow?.days || [])],
          start: c.workOrders.sendWindow?.start || "08:00",
          end: c.workOrders.sendWindow?.end || "20:00",
        }
      : emptyWorkOrders(),
    // A opção antiga abre como o formato que ela fazia; ao salvar, vai só o formato.
    contactName: c.contactName?.format
      ? { enabled: true, format: c.contactName.format }
      : c.workOrders?.prefixContactName === true
        ? { enabled: true, format: LEGACY_CONTACT_NAME_FORMAT }
        : emptyContactName(),
    crm: c.crm
      ? {
          enabled: true,
          boardId: c.crm.boardId ?? "",
          entry: c.crm.columns?.entry ?? "",
          inProgress: c.crm.columns?.inProgress ?? "",
          attention: c.crm.columns?.attention ?? "",
          completed: c.crm.columns?.completed ?? "",
          rules: (c.crm.rules || []).map((r) => ({
            uid: uid(),
            requirementKey: r.requirementKey || "",
            when: r.when || "awaiting",
            columnId: r.columnId ?? "",
          })),
        }
      : emptyCrm(),
    customerExport: c.customerExport
      ? { enabled: true, integrationId: c.customerExport.integrationId ?? "", items: c.customerExport.items || [] }
      : emptyCustomerExport(),
    warrantySplit: c.warrantySplit
      ? {
          enabled: true,
          whenUnknown: c.warrantySplit.whenUnknown === "in" ? "in" : "out",
          texts: Object.fromEntries(
            WARRANTIES.map((w) => [
              w,
              {
                completion: c.warrantySplit.texts?.[w]?.completion || "",
                supportText: c.warrantySplit.texts?.[w]?.supportText || "",
              },
            ])
          ),
        }
      : emptyWarrantySplit(),
    followUp: c.followUp
      ? (() => {
          const final = c.followUp.finalAfterMinutes ? tempoNaTela(c.followUp.finalAfterMinutes) : { amount: 3, unit: "days" };
          return {
            enabled: true,
            stages: (c.followUp.stages || []).map((st) => ({
              uid: uid(),
              ...tempoNaTela(st.afterMinutes),
              message: st.message || "",
              template: templateDaTela(st.template),
            })),
            finalAction: c.followUp.finalAction || "wait",
            finalAmount: final.amount,
            finalUnit: final.unit,
            days: [...(c.followUp.sendWindow?.days || [])],
            start: c.followUp.sendWindow?.start || "08:00",
            end: c.followUp.sendWindow?.end || "20:00",
          };
        })()
      : emptyFollowUp(),
  };
};

const opcional = (v) => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const idOuNulo = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

/** Um dado da regra como vai ao servidor (o estado só compara a sigla inteira). */
const dadoDaRegra = (c) => ({ field: c.field, match: c.field === "state" ? "equals" : c.match, values: routeValues(c.valuesText) });

/** A opção de itens por garantia como vai ao servidor: só os textos escritos. */
const garantiaNoEnvio = (split) => {
  const texts = {};
  WARRANTIES.forEach((w) => {
    const completion = opcional(split.texts[w].completion);
    const supportText = opcional(split.texts[w].supportText);
    if (completion || supportText) texts[w] = { ...(completion && { completion }), ...(supportText && { supportText }) };
  });
  return { whenUnknown: split.whenUnknown, ...(Object.keys(texts).length > 0 && { texts }) };
};

/** Estado da tela → corpo do POST/PUT (name, description, config). */
export const toPayload = (form) => ({
  name: form.name.trim(),
  description: form.description.trim() || null,
  config: {
    ai: {
      model: form.model,
      replyMode: form.replyMode,
      waitTimeSeconds: form.waitTimeSeconds === "" ? undefined : Number(form.waitTimeSeconds),
      persona: opcional(form.persona),
      supportText: opcional(form.supportText),
    },
    aiUserId: form.aiUserId === "" || form.aiUserId === null ? null : Number(form.aiUserId),
    requirements: form.items.map((it, i) => ({
      key: it.key,
      name: it.name.trim(),
      description: opcional(it.description),
      inputType: it.inputType,
      required: !!it.required,
      order: i + 1,
      aiInstruction: opcional(it.aiInstruction),
      requestMessage: it.requestMessage.trim(),
      retryMessage: opcional(it.retryMessage),
      // Vazio: o backend aplica o padrão (3); zero ou fora de 1–10 volta com erro.
      maxAttempts: it.maxAttempts === "" ? undefined : Number(it.maxAttempts),
      onMaxAttempts: it.onMaxAttempts,
      workOrderField: form.workOrders.enabled && it.inputType === "text" && it.workOrderField ? it.workOrderField : null,
      ...(it.isProblem && { isProblem: true }),
      ...(form.warrantySplit.enabled && it.warranty && { warranty: it.warranty }),
      extraction: it.extraction.enabled && acceptsExtraction(it.inputType)
        ? {
            enabled: true,
            minConfidence: (it.extraction.minConfidence === "" ? 80 : Number(it.extraction.minConfidence)) / 100,
            fields: it.extraction.fields.map((f) => ({
              key: f.key,
              label: f.label.trim(),
              type: f.type,
              required: !!f.required,
              sensitive: !!f.sensitive,
              ...(form.workOrders.enabled && it.inputType !== "text" && f.compareWith && { compareWith: f.compareWith }),
            })),
          }
        : null,
    })),
    messages: { intro: opcional(form.intro), completion: form.completion.trim() },
    onComplete: {
      ticketAction: form.ticketAction,
      ...(form.ticketAction === "keep_pending" && {
        queueId: idOuNulo(form.completionQueueId),
        userId: idOuNulo(form.completionUserId),
      }),
    },
    onHandoff: { queueId: idOuNulo(form.handoffQueueId), userId: idOuNulo(form.handoffUserId) },
    // Só quando há regras: a triagem de antes, reaberta, não fica com mudanças não publicadas.
    ...(form.workOrderRoutes.length > 0 && {
      workOrderRoutes: form.workOrderRoutes.map((r) => ({
        ...dadoDaRegra(r),
        // Só com mais de um dado: a regra de antes fica igual.
        ...(r.also.length > 0 && { also: r.also.map(dadoDaRegra) }),
        queueId: idOuNulo(r.queueId),
        userId: idOuNulo(r.userId),
      })),
    }),
    // Vazio: o backend aplica o padrão (30); fora de 0–365 volta com erro.
    expireAfterDays: form.expireAfterDays === "" ? undefined : Number(form.expireAfterDays),
    outreach:
      form.outreach.whatsappId === ""
        ? null
        : {
            whatsappId: Number(form.outreach.whatsappId),
            template: form.outreach.template
              ? {
                  name: form.outreach.template.name,
                  language: form.outreach.template.language,
                  variables: form.outreach.template.variables || {},
                }
              : null,
          },
    workOrders: form.workOrders.enabled
      ? {
          sourceId: Number(form.workOrders.sourceId) || null,
          startMode: form.workOrders.startMode,
          statuses: form.workOrders.statuses,
          types: form.workOrders.types,
          sendWindow: {
            days: [...form.workOrders.days].sort((a, b) => a - b),
            start: form.workOrders.start,
            end: form.workOrders.end,
          },
        }
      : null,
    followUp: form.followUp.enabled
      ? {
          stages: form.followUp.stages.map((st) => ({
            afterMinutes: toMinutes(st.amount, st.unit),
            message: st.message.trim(),
            template: templateDaTela(st.template),
          })),
          finalAction: form.followUp.finalAction,
          finalAfterMinutes:
            form.followUp.finalAction === "handoff" ? toMinutes(form.followUp.finalAmount, form.followUp.finalUnit) : null,
          sendWindow: {
            days: [...form.followUp.days].sort((a, b) => a - b),
            start: form.followUp.start,
            end: form.followUp.end,
          },
        }
      : null,
    crm: form.crm.enabled
      ? {
          boardId: Number(form.crm.boardId) || null,
          columns: {
            entry: Number(form.crm.entry) || null,
            inProgress: Number(form.crm.inProgress) || null,
            attention: Number(form.crm.attention) || null,
            completed: Number(form.crm.completed) || null,
          },
          rules: form.crm.rules.map((r) => ({
            requirementKey: r.requirementKey,
            when: r.when,
            columnId: Number(r.columnId) || null,
          })),
        }
      : null,
    // Só quando ligado: a triagem de antes, reaberta, não fica "com alterações".
    ...(form.customerExport.enabled && {
      customerExport: {
        integrationId: Number(form.customerExport.integrationId) || null,
        // Na ordem dos itens; o item que saiu da triagem sai daqui (como o servidor faz).
        items: form.items.map((it) => it.key).filter((k) => k && form.customerExport.items.includes(k)),
      },
    }),
    // Só quando ligado: a triagem de antes, reaberta, não fica "com alterações".
    ...(form.contactName.enabled && { contactName: { format: form.contactName.format.trim() } }),
    ...(form.warrantySplit.enabled && { warrantySplit: garantiaNoEnvio(form.warrantySplit) }),
  },
});

/** Assinatura para saber se há mudanças não salvas (ignora o que é só da tela). */
export const snapshot = (form) => JSON.stringify(toPayload(form));

export const INPUT_TYPES = ["text", "image", "document", "document_or_image", "video", "audio"];
// Vídeo e áudio não têm extração de campos: a IA descreve o vídeo e transcreve a fala.
export const acceptsExtraction = (inputType) => !["video", "audio"].includes(inputType);
export const FIELD_TYPES = ["string", "number", "date", "cpf_cnpj", "nfe_key", "phone", "email"];
export const ON_MAX = ["handoff", "waive", "keep_asking"];
